import { caseInsensitiveComparator } from './strings';

export const TEMP_ID = 'tmp-sensor-id-';
// Lazy-loaded DOM parser reference
let parser;

export const SENSOR_SOURCE_TYPES = {
    associatedSystems: 'Check Associated Systems',
    equipmentTarget:   'Check Equipment Target',
    siblingEquipment:  'Check Sibling Equipment',
    systemEquipment:   'Check System Equipment',
    systemTarget:      'Check System Target'
};

export const SENSOR_SOURCE_KEYS =
    Object.keys(SENSOR_SOURCE_TYPES)
        .reduce((keys, type) => ({
            ...keys,
            [type]: type
        }), {});

export const SENSOR_SOURCING_OPTIONS = {
    General: {
        Equipment: [
            SENSOR_SOURCE_KEYS.equipmentTarget,
            SENSOR_SOURCE_KEYS.associatedSystems,
            SENSOR_SOURCE_KEYS.siblingEquipment
        ],
        System: [
            SENSOR_SOURCE_KEYS.systemTarget,
            SENSOR_SOURCE_KEYS.systemEquipment
        ]
    },
    Equipment: {
        Equipment: [
            SENSOR_SOURCE_KEYS.equipmentTarget,
            SENSOR_SOURCE_KEYS.siblingEquipment
        ]
    }
};

export function isPersistedSensor(sensor = {}) {
    return !isTempSensor(sensor);
}

export function isTempSensor({ id } = {}) {
    return String(id).startsWith(TEMP_ID);
}

/**
 * Encodes a list of sensors into a format suitable for a Blockly FieldDropdown
 */
export function encodeSensorOptions(sensors) {
    return JSON.stringify(
        sensors
            .filter(isPersistedSensor)
            .map(sensorToOption)
            .sort(caseInsensitiveComparator)
    );
}

/**
 * Returns a human readable name for a given sensor
 */
export function getSensorName(sensor) {
    const {
        equipmentTypeName,
        id,
        measurement,
        multiSensorOption,
        ruleName,
        sensorAggregateDuration,
        sensorAggregateType,
        sensorTypeName,
        sourceType,
        systemTypeName
    } = sensor;

    switch (sourceType) {
        case 'Equipment':
            if (sensorAggregateType !== 'None') {
                return `${sourceType} | ${equipmentTypeName} | ${sensorTypeName} | ${multiSensorOption} | ${sensorAggregateType}: ${sensorAggregateDuration}`;
            }
            return `${sourceType} | ${equipmentTypeName} | ${sensorTypeName} | ${multiSensorOption}`;
        case 'General':
            if (sensorAggregateType !== 'None') {
                return `${sourceType} | ${sensorTypeName} | ${multiSensorOption} | ${sensorAggregateType}: ${sensorAggregateDuration}`;
            }
            return `${sourceType} | ${sensorTypeName} | ${multiSensorOption}`;
        case 'Rule':
            if (sensorAggregateType !== 'None') {
                return `${sourceType} | ${ruleName} | ${multiSensorOption} | ${sensorAggregateType}: ${sensorAggregateDuration}`;
            }
            return `${sourceType} | ${ruleName} | ${multiSensorOption}`;
        case 'System':
            if (sensorAggregateType !== 'None') {
                return `${sourceType} | ${systemTypeName} | ${sensorTypeName} | ${multiSensorOption} | ${sensorAggregateType}: ${sensorAggregateDuration}`;
            }
            return `${sourceType} | ${systemTypeName} | ${sensorTypeName} | ${multiSensorOption}`;
        case 'Weather':
            if (sensorAggregateType !== 'None') {
                return `${sourceType} | ${measurement} | ${sensorAggregateType}: ${sensorAggregateDuration}`;
            }
            return `${sourceType} | ${measurement}`;
        default:
            return id;
    }
}

/**
 * Returns a Blockly FieldDropdown options array for a given sensor.
 */
export function sensorToOption(sensor) {
    let label = getSensorName(sensor);

    return [label, sensor.id];
}

/**
 * Performs transforms required for saving a sensor
 */
export function prepForSave(sensors = []) {
    return sensors.map((sensor) => {
        if (isPersistedSensor(sensor)) {
            return sensor;
        }
        return {
            ...sensor,
            id: undefined
        };
    });
}

/**
 * Rewrites all of the sensor mutation nodes in the provided Blockly Workspace
 * XML to ensure that they stay in sync with the available sensors.
 */
export function syncSensorsXml(sensors, xml = '') {
    const syncXml = xml.trim();
    if (!syncXml) {
        return syncXml;
    }
    sensors = sensors.filter(isPersistedSensor);
    const sensorOptions = sensors.map(sensorToOption);
    const sensorIds = sensors.map(({ id }) => id);

    // Lazy load the DOM/XML Parser
    parser || (parser = new window.DOMParser());
    const doc = parser.parseFromString(syncXml, 'text/xml');

    const mutations = doc.documentElement
        .querySelectorAll('block[type="sensor_ref"] mutation');

    // Rebuild dropdown options mutations
    mutations.forEach(mutation => {
        const newMutationOptions = [...sensorOptions];
        const optionAttrs = mutation.getAttribute('options') || '[]';
        const options = JSON.parse(optionAttrs);
        const placeholder = options[0] && !options[0][1];
        if (placeholder) {
            newMutationOptions.unshift(options[0]);
        }
        mutation.setAttribute('options', JSON.stringify(newMutationOptions));
    });

    // Expunge removed Sensor IDs from field values
    const fieldValues = doc.documentElement.querySelectorAll('block[type="sensor_ref"] field[name="VALUE"]');
    fieldValues.forEach(field => {
        if (!sensorIds.includes(field.innerHTML)) {
            field.innerHTML = '';
        }
    });

    return doc.documentElement.outerHTML;
}
